import './styles/tokens.css';
import './styles/modals.css';
import './app/Layout.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './app/App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Sensible defaults for a dense internal console.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
});

async function bootstrap(): Promise<void> {
  // Dev-only escape hatch: `VITE_USE_MOCK_API=1 npm run dev` boots the MSW
  // browser worker so the SPA keeps running offline against the same fixture
  // data the Vitest suite uses. When the env flag is unset every screen talks
  // to `sentinel-api` through the `/api` proxy.
  if (import.meta.env.VITE_USE_MOCK_API === '1') {
    const { worker } = await import('./app/mockWorker');
    await worker.start({ onUnhandledRequest: 'bypass' });
    // Give the freshly registered service worker a chance to claim the
    // current client. Without this, the first page's useQuery fetches can
    // race past MSW's interception and land as net::ERR_ABORTED.
    if ('serviceWorker' in navigator) {
      await navigator.serviceWorker.ready;
    }
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            // Opt in to v7 behaviour early — silences the two
            // React-Router v6 migration warnings that were cluttering
            // the dev console on every route change.
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
